import Vue from "vue";
import Router from "vue-router";
import * as Sentry from "@sentry/browser";

import api from "../api";
import Socket from "../plugins/socket";
import { getCountryFromService } from "@/helpers/phone";
import Account from "../api/account";

import StorageWrapper from "@/helpers/storage";

const HomePage = () => import("../components/HomePage.vue");
const LoadingPage = () => import("../components/LoadingPage.vue");
const CustomersPage = () => import("../components/CustomersPage.vue");
const CustomerPage = () => import("../components/CustomerPage.vue");
const UsersPage = () => import("../components/UsersPage.vue");
const UserPage = () => import("../components/UserPage.vue");
const OrganizationPage = () => import("../components/OrganizationPage.vue");
const ConversationsPage = () => import("../components/ConversationsPage.vue");
const ProjectsPage = () => import("../components/ProjectsPage.vue");
const ConversationPage = () => import("../components/ConversationPage.vue");
const ProjectPage = () => import("../components/ProjectPage.vue");
const SubscriptionPage = () => import("../components/SubscriptionPage.vue");
const PaymentSuccess = () => import("../components/PaymentSuccess.vue");
const AttributesPanel = () => import("../components/AttributesPanel.vue");
const ActionUploadPhoto = () => import("../components/ActionUploadPhoto.vue");
const ActionPhoto = () => import("../components/ActionPhoto.vue");
const ActionStartRecording = () => import("../components/ActionStartRecording.vue");
const ActionVideo = () => import("../components/ActionVideo.vue");
const ActionSchedule = () => import("../components/ActionSchedule.vue");
const AppStorePage = () => import("../components/AppStorePage.vue");
const NotificationsPage = () => import("../components/NotificationsPage.vue");
Vue.use(Router);

// ignore errors on re-navigate
const originalPush = Router.prototype.push;
Router.prototype.push = function push(location) {
	return originalPush.call(this, location).catch((err) => err);
};

const router = new Router({
	mode: "history",
	scrollBehavior(to, from, savedPosition) {
		if (to.hash) {
			// BEFORE:
			return { selector: to.hash, behavior: "smooth" };

			//return { el: to.hash };
		}
	},
	routes: [
		{
			path: "/",
			name: "home",
			component: HomePage,
		},
		{
			path: "/callback",
			name: "callback",
			component: LoadingPage,
		},
		{
			path: "/payment-success",
			name: "paymentSuccess",
			component: PaymentSuccess,
		},
		{
			path: "/logout",
			name: "logout",
			component: HomePage,
		},
		{
			path: "/contacts",
			name: "contacts",
			component: CustomersPage,
		},
		{
			path: "/contacts/:id",
			name: "contact",
			meta: {
				entityTableName: "customers",
			},
			components: {
				default: CustomerPage,
				inner: AttributesPanel,
			},
		},
		{
			path: "/users",
			name: "users",
			component: UsersPage,
		},
		{
			path: "/users/:id",
			name: "user",
			component: UserPage,
		},
		{
			path: "/organizations/:id",
			name: "organization",
			component: OrganizationPage,
		},
		{
			path: "/subscriptions/:id",
			name: "subscription",
			component: SubscriptionPage,
		},
		{
			path: "/conversations",
			name: "conversations",
			components: {
				default: ConversationsPage,
			},
		},
		{
			path: "/projects",
			name: "projects",
			components: {
				default: ProjectsPage,
			},
		},
		{
			path: "/apps",
			name: "apps",
			components: {
				default: AppStorePage,
			},
		},
		{
			path: "/conversations/:id",
			name: "conversation",
			meta: {
				entityTableName: "conversations",
			},
			components: {
				default: ConversationPage,
				inner: AttributesPanel,
			},
		},
		{
			path: "/projects/:id",
			name: "project",
			meta: {
				entityTableName: "projects",
			},
			components: {
				default: ProjectPage,
			},
			props: (route) => ({
				projectId: route.params.id,
				shareId: route.query.shareId,
			}),
		},
		{
			path: "/projects/:id/:sharingId",
			name: "project-shared",
			meta: {
				entityTableName: "projects",
			},
			components: {
				default: ProjectPage,
			},
			props: (route) => ({
				projectId: route.params.id,
				shareId: route.params.shareId,
			}),
		},
		{
			path: "/conversations/ical/:id",
			name: "conversation-ical",
			component: HomePage,
		},
		{
			path: "/upload",
			name: "upload-photo",
			component: ActionUploadPhoto,
			props: (route) => ({
				conversationType: "sync",
				allowJoin: true,
				showHeader: true,
				projectId: route.query.projectId, // Extract `projectId` from the query parameters
			}),
		},
		{
			path: "/photo",
			name: "photo",
			component: ActionPhoto,
			props: (route) => ({
				conversationType: "sync",
				allowJoin: true,
				showHeader: true,
				projectId: route.query.projectId, // Extract `projectId` from the query parameters
			}),
		},
		{
			path: "/async-recording",
			name: "async-recording",
			component: ActionStartRecording,
			props: (route) => ({
				conversationType: "sync",
				allowJoin: true,
				showHeader: true,
				projectId: route.query.projectId, // Extract `projectId` from the query parameters
			}),
		},
		{
			path: "/live-call",
			name: "live-call",
			component: ActionVideo,
			props: (route) => ({
				conversationType: "sync",
				allowJoin: true,
				showHeader: true,
				projectId: route.query.projectId, // Extract `projectId` from the query parameters
			}),
		},
		{
			path: "/schedule",
			name: "schedule",
			component: ActionSchedule,
			props: (route) => ({
				conversationType: "sync",
				allowJoin: true,
				showHeader: true,
				projectId: route.query.projectId, // Extract `projectId` from the query parameters
			}),
		},
		{
			path: "/request-recording",
			name: "request-recording",
			component: ActionVideo,
			props: (route) => ({
				conversationType: "async",
				allowJoin: false,
				showHeader: true,
				projectId: route.query.projectId, // Extract `projectId` from the query parameters
			}),
		},
		{
			path: "/notifications",
			name: "notifications",
			component: NotificationsPage,
		},
		{
			path: "*",
			name: "notfound",
			component: HomePage,
		},
	],
});

// very basic "setup" of a global guard
router.beforeEach(async (to, from, next) => {
	if (to.name == "callback") {
		// check if "to"-route is "callback" and allow access
		router.app.$auth
			.handleAuthentication()
			.then(function () {
				// weird load order issues on the main page
				// causes a timing issue here, so just reload one final time after we authenticate
				let path = StorageWrapper.getItem("login-path");
				window.location.href = path || "/";
				// TODO: could store/retrieve path here
			})
			.catch(function () {
				if (!router.app.$auth.isBlocked) {
					router.app.$auth.login({
						path: to.path,
					});
				}
			});
	} else if (to.name.indexOf("conversation-ical") != -1 && router.app.$auth.isAuthenticated()) {
		if (to.params.id) {
			const conversationId = to.params.id;
			const conversation = await api.getConversation(conversationId);

			if (conversation) {
				window.location.href = conversation.creatorUrl;
			} else {
				console.warn(`Conversation: Status=NotFound, Id=${conversationId}`);
			}
		}
	} else if (router.app.$auth.isAuthenticated()) {
		// if authenticated allow access
		// redirect some domains...would be nice to do this elsewhere
		// we allow the app to be authenticated, then redirect after
		const redirectFrom = ["app.supbubble.com", "contact.liveswitch.com"];
		const redirectTo = "app.contact.liveswitch.com";
		for (let i = 0; i < redirectFrom.length; i++) {
			// check root url
			if (window.location.host == redirectFrom[i]) {
				let target = new URL("https://" + redirectFrom[i]);
				target.hostname = redirectTo;
				window.location.href = target;
				// bail out of the whole function without invoking next()
				return;
			}
		}

		// check if we're loaded via the api
		if (!router.app.$loaded) {
			let me = null;
			try {
				me = await api.getMe();
				if (!me) {
					// should not happen, as this only runs after auth
					alert("What...");
				}
			} catch (e) {
				//console.log(e)
				if (e.code == 401) {
					// we don't can't find a user in the database. Show "no account" modal
					router.app.$root.$noAccount.open();
					return;
				} else {
					if (e.code) {
						alert(e.error); // + ' (Response code: ' + e.code + ')')
					} else {
						if (window.location.hostname == "localhost") {
							alert("Could not reach the server. Please make sure the API service is running.");
						}
					}
				}
			}
			router.app.$loaded = true;

			// SENTRY CONTEXT AREA
			if (me) {
				// Populate the sentry context for what ever information we know at this time.
				Object.keys(me).forEach((key) => {
					if (!Array.isArray(me[key])) {
						Sentry.setContext(key, me[key]);
					}
				});
				// This is a special extra setup to help track the user.
				if (me.user) {
					// Track the current user (allows seeing multiple errors for a user).
					Sentry.setUser({ id: me.user.id ?? "", email: me.user.email ?? "", username: me.user.email ?? "" });
				}
			}
			// This will give you information of the last path they went to and came from.
			if (to) Sentry.setContext("to", to);
			if (from) Sentry.setContext("from", from);
			// END SENTRY CONTEXT AREA

			// no orgs means we move to the setup
			if (me.organization && me.organization.id) {
				console.log("starting");
				api.setOrganizationId(me.organization.id);
				// A single time operation to setup the default country code for organizations.
				if (!me?.organization?.defaultCountryCode) {
					const account = new Account(me.account);
					const hasPermissionToUpdateOrg = account.hasPermissionTo("UPDATE_ORGANIZATION");
					if (hasPermissionToUpdateOrg && me?.organization) {
						// Will grab their country code from the service.
						me.organization.defaultCountryCode = await getCountryFromService();
						if (me?.organization?.defaultCountryCode) {
						// Lets update the default value for their organization based on the above value. 
							api.updateOrganization(me.organization.id, {
								defaultCountryCode: me.organization.defaultCountryCode
							}).then(x => {
								if (me.organization) {
									me.organization.defaultCountryCode = x.defaultCountryCode;
								}
							}).catch(err => {
								console.error("Failed to update organization country code", err);
							});
						}
					}
				}
				// This will add their default country to the preferred country list in the telephone dropdown.
				if (!window.VueTelProps.preferredCountries.some((c) => c.localeCompare(me.organization.defaultCountryCode, { sensitivity: "base" }) == 0)) {
					window.VueTelProps.preferredCountries = [me.organization.defaultCountryCode, ...window.VueTelProps.preferredCountries];
				}
				router.app.$root.$organization = me.organization;
				//StorageWrapper.setItem("org", JSON.stringify(me.organization));
				//const params = new Proxy(new URLSearchParams(window.location.search), {
				//    get: (searchParams, prop) => searchParams.get(prop),
				//  });

				try {
					/*  
						Connect to the socket to receive live updates.
						It is not criticial to the functionality of Contact, so if it fails it fails.
					*/
					if (!Socket.connected) {
						Socket.auth = {
							userId: me.user.id,
							organizationId: me.organization.id,
						};
						Socket.connect();
					}
				} catch (e) {
					Sentry.captureException(e);
				}
				next();
			} else {
				router.app.$root.$noAccount.open();
				return;
			}
		} else {
			console.log("routing");
			next();
		}
	} else if (to.path.indexOf("projects/") != -1 && to.fullPath.indexOf("shareId") != -1) {
		// show the insecure project page
		const projectId = to.fullPath.split("/projects/")[1].split("?")[0];
		const shareId = to.fullPath.split("shareId=")[1].split("&")[0];
		try {
			const project = await api.getProjectInsecure(projectId, shareId);
			if (project) {
				next();
			} else {
				StorageWrapper.setItem("login-path", to.path || window.location.pathname);
				router.app.$auth.login({
					path: to.path,
				});
			}
		} catch (e) {
			console.error("Failed to load project:", e);
			next();
		}
	} else {
		// trigger auth0 login
		const goto = to.path || window.location.pathname;
		if (StorageWrapper.getItem("login-path")) {
			router.app.$auth.login({
				path: StorageWrapper.getItem("login-path"),
			});
			
			if (goto.indexOf("/logout") == -1) {
				StorageWrapper.setItem("login-path", goto);
			}
		} else {
			StorageWrapper.setItem("login-path", goto);
			router.app.$auth.login({
				path: goto,
			});
		}
	}
});

/* Capture and log router errors.  */
router.onError((error, to) => {
	try {
		Sentry.captureException(error, {
			tags: {
				file: "router/index.js",
			},
			extra: {
				to: to,
			},
		});
	} catch (e) {
		console.warn("error while capturing exception: ", e);
	}
	if (
		error.message.includes("Failed to fetch dynamically imported module") ||
		error.message.includes("Failed to load module script")
	) {
		window.location.reload();
	}
});

export default router;
